/**
 * Runtime theme behaviour. Owns `deviceMode` (the browser presentation
 * preference, seeded from Cache A), the effective-mode source selection, the
 * System media listener, release gating, and the SINGLE synchronization path
 * that keeps `deviceMode` + Cache A coherent and applies the document theme.
 *
 * NO server I/O. `useAppearanceSettings` is the sole owner of the canonical
 * authenticated member preference; `useTheme.mode` selects between that and
 * `deviceMode` by `hasActiveMemberContext`. See
 * `_docs/architecture/theme-system.md` §2.
 *
 * Module-level singleton.
 */

import { computed, ref, watch } from 'vue';
import { THEME_FEATURE_RELEASED } from '@/config/themeFeature';
import {
  applyThemeToDocument,
  normalizeThemeMode,
  readThemeModeFromCache,
  resolveTheme,
  writeThemeModeToCache,
} from '@/utils/themePreference';
import { useAppearanceSettings } from './useAppearanceSettings';

const appearance = useAppearanceSettings();

const deviceMode = ref(normalizeThemeMode(readThemeModeFromCache()));
const systemPrefersDark = ref(false);
const resolvedTheme = ref('light');

/**
 * Effective theme mode:
 *   authenticated member context active -> the canonical member `themeMode`
 *   otherwise (anonymous / logged out / transient login window) -> `deviceMode`
 */
const mode = computed(() =>
  appearance.hasActiveMemberContext.value
    ? appearance.themeMode.value
    : deviceMode.value,
);

let mediaQuery = null;
let mediaListener = null;
let started = false;
let stopWatch = null;

/**
 * The ONE path that (a) mirrors the effective mode into `deviceMode`, (b) writes
 * Cache A, (c) computes `resolvedTheme`, (d) applies the document. Keyed on the
 * EFFECTIVE mode — the `deviceMode` mirror below cannot change the effective
 * mode when a member source is active, and is a no-op assignment when anonymous,
 * so this never re-enters or double-applies.
 */
function synchronizeTheme() {
  const effective = normalizeThemeMode(mode.value);
  if (deviceMode.value !== effective) {
    deviceMode.value = effective;
  }
  writeThemeModeToCache(effective);
  const resolved = resolveTheme(effective, systemPrefersDark.value, THEME_FEATURE_RELEASED);
  resolvedTheme.value = resolved;
  applyThemeToDocument(resolved);
}

/** Delegation only — no DOM write, no Cache A write, no `deviceMode` write while a member context is active. */
function setMode(next) {
  const normalized = normalizeThemeMode(next);
  if (appearance.hasActiveMemberContext.value) {
    appearance.setThemeMode(normalized);
  } else {
    deviceMode.value = normalized;
  }
}

function attachMediaListener() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
  try {
    mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    systemPrefersDark.value = mediaQuery.matches === true;
    mediaListener = (event) => {
      systemPrefersDark.value = event.matches === true;
    };
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', mediaListener);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(mediaListener); // Safari < 14
    }
  } catch {
    mediaQuery = null;
    mediaListener = null;
  }
}

export function initTheme() {
  if (started) return;
  started = true;
  attachMediaListener();
  stopWatch = watch([mode, systemPrefersDark], synchronizeTheme);
  synchronizeTheme();
}

/** Test-only: detach the media listener and reset so `initTheme` can run again. */
export function dispose() {
  if (mediaQuery && mediaListener) {
    if (typeof mediaQuery.removeEventListener === 'function') {
      mediaQuery.removeEventListener('change', mediaListener);
    } else if (typeof mediaQuery.removeListener === 'function') {
      mediaQuery.removeListener(mediaListener);
    }
  }
  if (stopWatch) stopWatch();
  mediaQuery = null;
  mediaListener = null;
  stopWatch = null;
  started = false;
}

export function useTheme() {
  return {
    deviceMode,
    mode,
    resolvedTheme,
    setMode,
    initTheme,
    dispose,
  };
}
